import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useLazyGetArticleFavoritesQuery,
  useAddArticleFavoriteMutation,
  useRemoveArticleFavoriteMutation,
} from 'services/articleFavoritesService';
import { useLoggedInUser } from 'features/auth/hooks/useAuthData';
import { LibraryArticleFavoriteAddRequest, TransformedLibraryArticle } from '../types';
import { toFavoritedArticles } from '../utils/libraryArticleFavoriteUtils';

// Stable fallback so the `favoritedArticles` memo doesn't recompute on every render.
const NO_LIBRARY_ARTICLES: TransformedLibraryArticle[] = [];

/**
 * @param libraryArticles Used to reuse library card data (e.g. image) for favorites that are
 * also in the library list; favorites outside it are still returned.
 */
export const useLibraryArticleFavorites = (
  libraryArticles: TransformedLibraryArticle[] = NO_LIBRARY_ARTICLES,
) => {
  const user = useLoggedInUser();
  const [fetchFavorites, { data: favorites = [], isLoading, error }] =
    useLazyGetArticleFavoritesQuery();
  const [addFavorite, { isLoading: isAdding }] = useAddArticleFavoriteMutation();
  const [removeFavorite, { isLoading: isRemoving }] = useRemoveArticleFavoriteMutation();
  // Keyed by url; holds a url only while its optimistic state hasn't been confirmed
  // (or rolled back) by the server yet.
  const [optimisticFavoritedByUrl, setOptimisticFavoritedByUrl] = useState<Record<string, boolean>>(
    {},
  );

  const loadFavorites = useCallback(async () => {
    try {
      await fetchFavorites().unwrap();
    } catch {
      // Error surfaced via the `error` field from the lazy query.
    }
  }, [fetchFavorites]);

  useEffect(() => {
    loadFavorites();
  }, [loadFavorites]);

  const { favoriteUrls, favoriteIdByUrl } = useMemo(() => {
    const urls = new Set<string>();
    const idsByUrl = new Map<string, number>();
    favorites.forEach((f) => {
      urls.add(f.url);
      idsByUrl.set(f.url, f.id);
    });
    return { favoriteUrls: urls, favoriteIdByUrl: idsByUrl };
  }, [favorites]);

  const clearOptimisticState = useCallback((url: string) => {
    setOptimisticFavoritedByUrl((prev) => {
      if (!(url in prev)) return prev;
      const next = { ...prev };
      delete next[url];
      return next;
    });
  }, []);

  const isFavorited = useCallback(
    (url: string) => optimisticFavoritedByUrl[url] ?? favoriteUrls.has(url),
    [optimisticFavoritedByUrl, favoriteUrls],
  );

  // Filtered by `isFavorited` so an un-favorite disappears before the refetch lands.
  const favoritedArticles = useMemo(
    () =>
      toFavoritedArticles(favorites, libraryArticles, user?.uuid ?? '').filter((article) =>
        isFavorited(article.contentUrl),
      ),
    [favorites, libraryArticles, user?.uuid, isFavorited],
  );

  const toggleFavorite = useCallback(
    async (input: LibraryArticleFavoriteAddRequest) => {
      const existingId = favoriteIdByUrl.get(input.url);
      const operation = existingId !== undefined ? 'removed' : 'added';

      // Reflect the new state immediately; roll back by clearing it (which falls
      // back to the last confirmed server state) if the mutation fails.
      setOptimisticFavoritedByUrl((prev) => ({ ...prev, [input.url]: existingId === undefined }));

      try {
        if (existingId !== undefined) {
          await removeFavorite(existingId).unwrap();
        } else {
          await addFavorite(input).unwrap();
        }
        await loadFavorites();
        clearOptimisticState(input.url);
        return operation;
      } catch (err) {
        clearOptimisticState(input.url);
        throw err;
      }
    },
    [favoriteIdByUrl, addFavorite, removeFavorite, loadFavorites, clearOptimisticState],
  );

  return {
    favorites,
    favoritedArticles,
    isLoading: isLoading || isAdding || isRemoving,
    error,
    loadFavorites,
    isFavorited,
    toggleFavorite,
  };
};
